import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';
import { Input } from '~/components';

const metricRangeConfig = {
  available_hours: {
    label: 'Available Hours',
    min: 0,
    maxByUnit: { day: 10, week: 50, month: 200 },
    step: 1,
  },
  billable_utilization: {
    label: 'Billable Utilization',
    min: 0,
    max: 125,
    step: 1,
    suffix: '%',
  },
  target_attainment: {
    label: 'Target Attainment',
    min: 0,
    max: 125,
    step: 1,
    suffix: '%',
  },
};

const getMetricRangeConfig = (metric, unit) => {
  const config = metricRangeConfig[metric];
  if (!config) return null;

  const max = config.max ?? config.maxByUnit?.[unit];

  return {
    ...config,
    max,
  };
};

const normalizeMetricRange = (range, config) => {
  if (!config) return null;

  const minValue = Number.isFinite(range?.min) ? range.min : config.min;
  const maxValue = Number.isFinite(range?.max) ? range.max : config.max;

  const clampedMin = Math.max(config.min, Math.min(Math.round(minValue), config.max));
  const clampedMax = Math.max(config.min, Math.min(Math.round(maxValue), config.max));

  const normalizedMin = Math.min(clampedMin, clampedMax);
  const normalizedMax = Math.max(clampedMin, clampedMax);

  return { min: normalizedMin, max: normalizedMax };
};

const Box = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  color: ${colors.black};
  background-color: transparent;
  border: 1px solid ${colors.grey40};
  border-radius: 0.3125rem;
  user-select: none;
`;

const Control = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 2rem;
  outline: none;
  box-shadow: none;

  &:focus-within {
    ${Box} {
      border: 1px solid ${colors.primary};
      border-radius: 0.3125rem;
    }
  }
`;

const TrackWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 2.5rem;
  right: 2.5rem;
  height: 0.25rem;
  transform: translateY(-50%);
  cursor: pointer;
`;

const Track = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  border-radius: 999rem;
  background: ${colors.grey20};
`;

const Range = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 999rem;
  background: ${colors.primary};
`;

const RangeInput = styled(Input)`
  position: absolute;
  top: 0;
  left: 2.5rem;
  right: 2.5rem;
  width: auto;
  height: 2rem;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  pointer-events: none;
  appearance: none;
  -moz-appearance: none;

  &::-webkit-slider-thumb {
    appearance: none;
    pointer-events: all;
    height: 0.75rem;
    width: 0.75rem;
    border-radius: 50%;
    border: 2px solid ${colors.primary};
    background: ${colors.white};
    margin-top: -0.3125rem;
  }

  &::-moz-range-thumb {
    -moz-appearance: none;
    pointer-events: all;
    height: 0.75rem;
    width: 0.75rem;
    box-sizing: border-box;
    border-radius: 50%;
    border: 2px solid ${colors.primary};
    background: ${colors.white};
    margin-top: 0;
    transform: translateY(-0.0625rem);
  }

  &::-webkit-slider-runnable-track {
    height: 0.25rem;
    background: transparent;
  }

  &::-moz-range-track {
    height: 0.25rem;
    background: transparent;
  }
`;

const ValueLabels = styled.div`
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  font-size: 0.625rem;
  color: ${colors.grey55};
  pointer-events: none;
`;

const MetricLabel = styled.div`
  position: absolute;
  top: 0;
  left: 0.625rem;
  margin-left: 1px;
  padding: 0 0.25rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;
`;

export default function MetricRangeFilter({ metric, unit, value, onChange }) {
  const config = useMemo(() => getMetricRangeConfig(metric, unit), [metric, unit]);

  const range = useMemo(() => (config ? normalizeMetricRange(value, config) : null), [value, config]);
  const [draftRange, setDraftRange] = useState(range);
  const trackRef = useRef(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (isDraggingRef.current) return;
    setDraftRange(range);
  }, [range]);

  const handleRangeChange = useCallback(() => {
    isDraggingRef.current = false;
    const nextRange = normalizeMetricRange(draftRange, config);
    if (!nextRange) return;

    onChange({
      min: nextRange.min === config.min ? null : nextRange.min,
      max: nextRange.max === config.max ? null : nextRange.max,
    });
  }, [config, draftRange, onChange]);

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    handleRangeChange();
  }, [handleRangeChange]);

  const handleTrackPointer = useCallback(
    (event) => {
      if (!config || !trackRef.current || !draftRange) return;

      isDraggingRef.current = true;

      const rect = trackRef.current.getBoundingClientRect();
      const percent = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);

      const rawValue = config.min + percent * (config.max - config.min);
      const steppedValue =
        config.step > 0 ? config.min + Math.round((rawValue - config.min) / config.step) * config.step : rawValue;

      const nextValue = Math.min(config.max, Math.max(config.min, steppedValue));

      const distanceToMin = Math.abs(nextValue - draftRange.min);
      const distanceToMax = Math.abs(nextValue - draftRange.max);

      const nextRange =
        distanceToMin <= distanceToMax
          ? normalizeMetricRange({ min: nextValue, max: draftRange.max }, config)
          : normalizeMetricRange({ min: draftRange.min, max: nextValue }, config);

      setDraftRange(nextRange);
    },
    [config, draftRange],
  );

  const handleMinChange = useCallback(
    (event) => {
      if (!draftRange || !config) return;
      isDraggingRef.current = true;
      const parsed = Number(event.target.value);

      if (!Number.isFinite(parsed)) return;
      const nextMin = Math.min(parsed, draftRange.max);
      const nextRange = normalizeMetricRange({ min: nextMin, max: draftRange.max }, config);

      if (!nextRange) return;
      setDraftRange(nextRange);
    },
    [config, draftRange],
  );

  const handleMaxChange = useCallback(
    (event) => {
      if (!draftRange || !config) return;
      isDraggingRef.current = true;
      const parsed = Number(event.target.value);

      if (!Number.isFinite(parsed)) return;
      const nextMax = Math.max(parsed, draftRange.min);
      const nextRange = normalizeMetricRange({ min: draftRange.min, max: nextMax }, config);

      if (!nextRange) return;
      setDraftRange(nextRange);
    },
    [config, draftRange],
  );

  if (!config || !range || !draftRange) return null;

  const { min: configMin, max: configMax, step, label, suffix } = config;
  const span = configMax - configMin;
  const ratio = span ? 100 / span : 0;
  const minPercent = (draftRange.min - configMin) * ratio;
  const maxPercent = (draftRange.max - configMin) * ratio;

  const formatLabel = (labelValue, { isMax = false } = {}) => {
    const displayValue = isMax && labelValue === configMax ? `${labelValue}+` : `${labelValue}`;
    if (suffix) return `${displayValue}${suffix}`;
    return displayValue;
  };

  return (
    <Control onMouseLeave={handleDragEnd}>
      <Box>
        <ValueLabels>
          <span>{formatLabel(draftRange.min)}</span>
          <span>{formatLabel(draftRange.max, { isMax: true })}</span>
        </ValueLabels>
        <TrackWrapper ref={trackRef} onMouseDown={handleTrackPointer}>
          <Track />
          <Range style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }} />
        </TrackWrapper>
        <RangeInput
          type="range"
          min={configMin}
          max={configMax}
          step={step}
          value={draftRange.min}
          onChange={handleMinChange}
          onMouseUp={handleRangeChange}
          onKeyUp={handleRangeChange}
        />
        <RangeInput
          type="range"
          min={configMin}
          max={configMax}
          step={step}
          value={draftRange.max}
          onChange={handleMaxChange}
          onMouseUp={handleRangeChange}
          onKeyUp={handleRangeChange}
        />
      </Box>
      <MetricLabel>{label}</MetricLabel>
    </Control>
  );
}
